import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getMyDashboard } from '../services/members';
import { initiateContribution } from '../services/cases';
import { getContributionAmount } from '../services/settings';
import { formatCurrency } from '../utils/formatters';
import { 
  FolderOpen, 
  Users, 
  Activity, 
  FileText, 
  Plus, 
  ArrowRight, 
  Loader2, 
  X, 
  Wallet,
  Bell,
  LogOut,
  Menu,
  User
} from 'lucide-react';

const Overview = () => {
  const { user, logout } = useAuth();
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberData, setMemberData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Contribution modal state
  const [showContributeModal, setShowContributeModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [contributionAmount, setContributionAmount] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => { 
    if (user) loadData(); 
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (user?.role === 'member' || !user?.role) {
        const data = await getMyDashboard();
        setMemberData(data);
        setRecentActivity(data.recentActivity || []);
      }
    } catch (err) { 
      console.error(err); 
    } finally { 
      setLoading(false); 
    }
  };

  const openContributeModal = async (caseObj) => {
    setSelectedCase(caseObj);
    setPhoneNumber('');
    setPaying(false);
    setShowContributeModal(true);
    if (!contributionAmount) {
      try {
        const amount = await getContributionAmount();
        setContributionAmount(amount);
      } catch (err) {
        console.error('Failed to fetch contribution amount', err);
        alert('Could not load contribution amount. Please try again later.');
        setShowContributeModal(false);
      }
    }
  };

  const handleContribute = async (e) => {
    e.preventDefault();
    const cleaned = phoneNumber.replace(/\D/g, '');
    let formattedPhone = cleaned;
    if (cleaned.length === 10 && cleaned.startsWith('07')) {
      formattedPhone = '254' + cleaned.slice(1);
    } else if (cleaned.length === 12 && cleaned.startsWith('2547')) {
      formattedPhone = cleaned;
    } else {
      alert('Please enter a valid Kenyan phone number (e.g., 0712345678 or 254712345678).');
      return;
    }

    setPaying(true);
    try {
      await initiateContribution(selectedCase.id, formattedPhone);
      alert('STK Push sent. Please enter your M-Pesa PIN on your phone.');
      setShowContributeModal(false);
      setPhoneNumber('');
      loadData(); 
    } catch (err) {
      alert(err.response?.data?.error || 'Payment initiation failed. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] px-4">
      <Loader2 className="animate-spin text-blue-600 mb-4" size={40} />
      <p className="text-slate-400 font-medium text-sm tracking-wide">Loading workspace dashboard...</p>
    </div>
  );

  const beneficiaries = memberData?.beneficiaries || 
                        memberData?.member?.beneficiaries || 
                        memberData?.member?.Beneficiaries || [];

  const memberName = memberData?.member?.full_name || user?.name || "Member";
  const memberNumber = memberData?.member?.member_number || "";

  return (
    <div className="w-full min-h-screen bg-[#f4f7fc] font-sans antialiased">
      {/* GLOBAL APP BAR – Responsive */}
      <div className="bg-white px-4 sm:px-8 py-3 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          {/* Mobile hamburger (optional) */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 -ml-2 text-slate-500 hover:text-blue-600 transition-colors"
          >
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-800 hidden sm:inline">WELFARE</span>
            <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full sm:hidden">NYM</span>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4 ml-auto">
          <button className="relative p-1.5 text-slate-500 hover:text-blue-600 transition-colors">
            <Bell size={20} className="sm:w-5 sm:h-5" />
            <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
          <button 
            onClick={logout} 
            className="flex items-center gap-1.5 text-slate-600 font-medium text-xs sm:text-sm hover:text-red-600 transition-colors p-1.5 rounded-lg hover:bg-red-50"
          >
            <LogOut size={16} className="sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm">
            {memberName.charAt(0)}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
        
        {/* WELCOME / IDENTITY BANNER – Responsive */}
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 sm:gap-4 bg-white p-4 sm:p-6 rounded-xl border border-slate-200/80 shadow-sm relative overflow-hidden">
          <div className="flex-1">
            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-slate-900 leading-tight">
              Hello, <span className="text-blue-600">{memberName}</span>
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Welcome back! Here's what's happening today.</p>
          </div>
          {memberNumber && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 self-start sm:self-center text-xs font-bold text-blue-700 whitespace-nowrap">
              <span>#{memberNumber}</span>
            </div>
          )}
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 self-start sm:self-center text-xs font-medium text-slate-600 shadow-sm">
            <span className="text-blue-600">📅</span>
            <span>{new Date().toLocaleDateString()}</span>
          </div>
          <div className="absolute bottom-0 left-0 w-[8%] h-[3px] bg-blue-600" />
        </div>

        {/* PRIMARY COLUMNS – Responsive Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          
          {/* YOUR RECENT CASES */}
          <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm flex flex-col overflow-hidden">
            <div className="p-3 sm:p-5 flex items-center gap-3 border-b border-slate-100">
              <div className="p-1.5 sm:p-2 bg-blue-50 text-blue-600 rounded-lg">
                <FolderOpen size={16} className="sm:w-5 sm:h-5" />
              </div>
              <h2 className="font-bold text-slate-800 text-sm sm:text-base">Your Recent Cases</h2>
            </div>

            <div className="p-4 sm:p-8 flex-grow flex flex-col items-center justify-center min-h-[200px] sm:min-h-[260px]">
              {memberData?.cases?.length > 0 ? (
                <div className="w-full space-y-3">
                  {memberData.cases.slice(0, 3).map(c => (
                    <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 p-3 sm:p-4 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="flex-1 min-w-[120px]">
                        <p className="font-semibold text-slate-800 capitalize text-sm">{c.type?.replace('_', ' ')}</p>
                        <p className="text-xs text-slate-400">Ref: #{c.id}</p>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className="font-mono font-bold text-sm text-slate-900">{formatCurrency(c.target_amount)}</span>
                        {c.status === 'open' && !c.has_contributed ? (
                          <button 
                            onClick={() => openContributeModal(c)} 
                            className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-emerald-700 transition-colors min-h-[36px]"
                          >
                            Contribute
                          </button>
                        ) : (
                          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded-md font-medium">Paid</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <FolderOpen size={28} className="text-blue-300 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-800 text-base">No cases yet.</h3>
                  <p className="text-slate-400 text-sm mt-1 mb-5">Click "New Case" to start.</p>
                  <Link to="/dashboard/member/cases/new" className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors shadow-sm shadow-blue-200">
                    <Plus size={16} /> New Case
                  </Link>
                </div>
              )}
            </div>

            <div className="bg-slate-50/60 p-3 sm:p-4 border-t border-slate-100 text-center">
              <Link to="/dashboard/member/cases" className="inline-flex items-center gap-1.5 text-blue-600 font-semibold text-xs hover:underline">
                View All Cases <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* BENEFICIARIES */}
          <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm flex flex-col overflow-hidden">
            <div className="p-3 sm:p-5 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-1.5 sm:p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                  <Users size={16} className="sm:w-5 sm:h-5" />
                </div>
                <h2 className="font-bold text-slate-800 text-sm sm:text-base">Beneficiaries</h2>
              </div>
              <Link to="/dashboard/member/beneficiaries" className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-600 hover:text-white transition-all min-h-[36px]">
                <Plus size={14} /> Add
              </Link>
            </div>

            <div className="p-3 sm:p-6 flex-grow overflow-y-auto max-h-[250px] sm:max-h-[300px]">
              {beneficiaries.length > 0 ? (
                <div className="space-y-2 sm:space-y-3">
                  {beneficiaries.map((b) => (
                    <div key={b.id || b.full_name} className="flex flex-wrap items-center justify-between gap-2 p-3 bg-white border border-slate-200/70 rounded-xl shadow-sm">
                      <div>
                        <p className="font-bold text-slate-800 text-sm">{b.full_name}</p>
                        <p className="text-xs text-slate-400 uppercase tracking-wider mt-0.5">{b.relationship || 'DEPENDENT'}</p>
                      </div>
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 rounded-md text-xs font-bold">
                        Active
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center py-6 text-center">
                  <Users className="text-slate-300 mb-2" size={28} />
                  <p className="text-slate-400 text-sm italic font-medium">No beneficiaries listed</p>
                </div>
              )}
            </div>

            <div className="bg-slate-50/60 p-3 sm:p-4 border-t border-slate-100 text-center mt-auto">
              <Link to="/dashboard/member/beneficiaries" className="inline-flex items-center gap-1.5 text-blue-600 font-semibold text-xs hover:underline">
                View All Beneficiaries <ArrowRight size={14} />
              </Link>
            </div>
          </div>

        </div>

        {/* RECENT ACTIVITY – Mobile-optimized */}
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden relative">
          <div className="p-3 sm:p-5 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-1.5 sm:p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                <Activity size={16} className="sm:w-5 sm:h-5" />
              </div>
              <div>
                <h2 className="font-bold text-slate-800 text-sm sm:text-base">Recent Activity</h2>
                <p className="text-xs text-slate-400 hidden sm:block">Here's what's been happening recently.</p>
              </div>
            </div>
            <Link to="/history" className="inline-flex items-center gap-1.5 text-blue-600 font-semibold text-xs hover:underline">
              View All <ArrowRight size={14} />
            </Link>
          </div>

          <div className="p-3 sm:p-8 flex flex-col items-center justify-center min-h-[180px] sm:min-h-[220px]">
            {recentActivity.length > 0 ? (
              <div className="w-full">
                {/* Mobile: card-style list */}
                <div className="space-y-3 sm:hidden">
                  {recentActivity.slice(0, 5).map((activity, i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{activity.description}</p>
                        <p className="text-xs text-slate-400">{activity.date}</p>
                      </div>
                      <span className="font-bold text-emerald-600 text-sm">{formatCurrency(activity.amount)}</span>
                    </div>
                  ))}
                </div>
                {/* Desktop: table */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-slate-100 text-slate-400 font-semibold">
                        <th className="py-2 px-4">Date</th>
                        <th className="py-2 px-4">Description</th>
                        <th className="py-2 px-4 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentActivity.slice(0, 10).map((activity, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                          <td className="py-3 px-4 text-slate-500 text-xs">{activity.date}</td>
                          <td className="py-3 px-4 font-medium text-slate-800 text-sm">{activity.description}</td>
                          <td className="py-3 px-4 text-right font-bold text-emerald-600">{formatCurrency(activity.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <FileText size={22} className="text-slate-300 mx-auto mb-2" />
                <h3 className="font-bold text-slate-800 text-base">No data available</h3>
                <p className="text-slate-400 text-sm mt-0.5">No transactions or activities found.</p>
              </div>
            )}
          </div>

          <div className="absolute bottom-0 left-0 w-full h-1 flex">
            <div className="w-2/3 h-full bg-blue-600"></div>
            <div className="w-1/3 h-full bg-red-600"></div>
          </div>
        </div>

      </div>

      {/* CONTRIBUTION MODAL – Mobile-optimized */}
      {showContributeModal && contributionAmount && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-2xl p-5 sm:p-8 w-full max-w-md shadow-xl relative animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setShowContributeModal(false)}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-all"
            >
              <X size={20} />
            </button>

            <div className="flex flex-col items-center text-center mb-5 sm:mb-6">
              <div className="w-11 h-11 sm:w-12 sm:h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center mb-3">
                <Wallet size={22} className="sm:w-6 sm:h-6" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">Make Contribution</h3>
              <p className="text-slate-400 text-xs mt-1">
                Support Case: <span className="font-semibold text-slate-700 capitalize">{selectedCase?.type?.replace('_', ' ')}</span>
              </p>
              <p className="text-xl sm:text-2xl font-black text-emerald-600 mt-3">{formatCurrency(contributionAmount)}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Fixed contribution amount</p>
            </div>

            <form onSubmit={handleContribute} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1.5 block ml-1">M-Pesa Phone Number</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={e => setPhoneNumber(e.target.value)}
                  placeholder="e.g., 0712345678"
                  autoFocus
                  required
                  className="w-full bg-slate-50 border border-slate-200 focus:border-emerald-500 focus:bg-white rounded-xl px-4 py-3 text-sm transition-all outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={paying}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 rounded-xl font-bold text-sm transition-all shadow-md shadow-emerald-100 active:scale-[0.99] disabled:opacity-50 min-h-[48px]"
              >
                {paying ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin" size={18} />
                    <span>Processing...</span>
                  </div>
                ) : (
                  'Confirm & Pay'
                )}
              </button>
              
              <p className="text-center text-[11px] text-slate-400 leading-normal">
                An STK Push prompt will be dispatched instantly to your phone.
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;